import { useState, useMemo } from "react";
import { useMode } from "@/context/ModeContext";
import { useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DollarSign, CheckCircle, Search } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";

import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import { MobileHeader } from "@/components/MobileHeader";
import { syncService } from "../services/SyncService";
import { usePagination } from "../hooks/usePagination";
import TablePagination from "../components/TablePagination";
import { formatRupees } from "../utils/currency";
import PropTypes from 'prop-types';
import { useUserRole } from "../hooks/useUserRole";
import { getAppointmentAccess } from "../utils/accessPolicy";

export default function FacilityCharges({ onLogout }) {
  const navigate = useNavigate();
  const { isLoading, memberships } = useUserRole();
  const membershipKey = (memberships || []).map(m => `${m.clinic_id}:${m.role}:${m.is_active}`).sort().join("|");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [paymentDate, setPaymentDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const doctorId = localStorage.getItem('user_id');

  const { activeMode } = useMode();

  const facility_charges = useLiveQuery(async () => {
    if (isLoading || !doctorId) return [];
    const allComms = await db.facility_charges.toArray();
    const allAppointments = await db.appointments.toArray();
    const apptMap = new Map(allAppointments.map(a => [a.id, a]));

    let comms = allComms.filter(c => {
      const apt = apptMap.get(c.appointment_id);
      if (apt) {
        const access = getAppointmentAccess(apt, doctorId, memberships);
        return access.canViewFee;
      }
      return c.associated_doctor_id === doctorId;
    });

    // Filter by Mode
    if (activeMode && activeMode !== 'everything') {
      if (activeMode === 'personal') {
        comms = comms.filter(c => {
          const apt = apptMap.get(c.appointment_id);
          const effectiveClinicId = (apt && apt.clinic_id) ? apt.clinic_id : c.clinic_id;
          return !effectiveClinicId || effectiveClinicId === 'personal';
        });
      } else {
        // Specific Clinic
        comms = comms.filter(c => {
          const apt = apptMap.get(c.appointment_id);
          const effectiveClinicId = (apt && apt.clinic_id) ? apt.clinic_id : c.clinic_id;
          return effectiveClinicId === activeMode;
        });
      }
    }

    const doctors = await db.users.toArray();
    const doctorMap = new Map(doctors.map(d => [d.id, d]));

    // Also fetch clinics so we can resolve the clinic owner for payable charges
    const clinicsList = await db.clinics.toArray();
    const clinicMap = new Map(clinicsList.map(c => [c.id, c]));

    return comms.map(c => {
      let receiver_clinic_name = null;
      let receiver_clinic_phone = null;

      if (c.clinic_id) {
        const clinic = clinicMap.get(c.clinic_id);
        if (clinic) {
          receiver_clinic_name = clinic.name;
          receiver_clinic_phone = clinic.phone || null;
        }
      }

      return {
        ...c,
        associated_doctor_name: doctorMap.get(c.associated_doctor_id)?.name || c.treating_doctor_name,
        associated_doctor_phone: doctorMap.get(c.associated_doctor_id)?.phone || c.treating_doctor_phone,
        receiver_doctor_name: receiver_clinic_name,
        receiver_doctor_phone: receiver_clinic_phone
      };
    });
  }, [doctorId, activeMode, isLoading, membershipKey]) || [];

  const handlePayment = async (e) => {
    e.preventDefault();
    try {
      const timestamp = new Date().toISOString();
      await db.facility_charges.where('id').anyOf(selectedIds).modify({
        paid_status: 'paid',
        payment_date: paymentDate,
        updated_at: timestamp,
        _synced: false
      });

      await syncService.addToQueue('facility_charge_payment', 'facility_charge_payment', {
        charge_ids: selectedIds,
        payment_date: paymentDate
      });

      toast.success("FacilityCharges marked as paid!");
      setShowPayDialog(false);
      setSelectedIds([]);
      setPaymentDate("");
    } catch (error) {
      console.error(error);
      toast.error("Failed to process payment");
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // Split facility_charges into payable (I'm being charged) and receivable (I'm the payee/from my appointments)
  const payableCommissions = facility_charges.filter(c => c.associated_doctor_id === doctorId);
  const receivableCommissions = facility_charges.filter(c => c.associated_doctor_id !== doctorId);

  const pendingPayable = payableCommissions.filter(c => c.paid_status === "pending");
  const paidPayable = payableCommissions
    .filter(c => c.paid_status === "paid")
    .sort((a, b) => new Date(b.payment_date || 0) - new Date(a.payment_date || 0));

  const filteredPending = useMemo(() => {
    if (!searchTerm) return pendingPayable;
    const term = searchTerm.toLowerCase();
    return pendingPayable.filter(c =>
      c.associated_doctor_name?.toLowerCase().includes(term) ||
      c.associated_doctor_phone?.includes(term)
    );
  }, [pendingPayable, searchTerm]);

  const filteredPaid = useMemo(() => {
    if (!searchTerm) return paidPayable;
    const term = searchTerm.toLowerCase();
    return paidPayable.filter(c =>
      c.associated_doctor_name?.toLowerCase().includes(term) ||
      c.associated_doctor_phone?.includes(term)
    );
  }, [paidPayable, searchTerm]);

  const receivablePagination = usePagination(receivableCommissions, {
    initialPageSize: 10,
    isMobile: window.innerWidth < 768
  });

  const pendingPagination = usePagination(filteredPending, {
    initialPageSize: 10,
    isMobile: window.innerWidth < 768
  });

  const paidPagination = usePagination(filteredPaid, {
    initialPageSize: 10,
    isMobile: window.innerWidth < 768
  });

  return (
    <div className="page-container" data-testid="facility_charges-page">
      <Sidebar active="facility_charges" onLogout={onLogout} />
      <div className="main-content">
        <div className="header hidden md:flex">
          <h1>Facility Charges</h1>
        </div>

        <MobileHeader title="Facility Charges" />

        <div className="search-bar mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by doctor name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="facility_charge-search-input"
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          {/* Receivable Card - Show only if user has received facility_charges */}
          {receivableCommissions.length > 0 && (
            <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b' }}>My Facility Earnings</h3>
              <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981', margin: '0.5rem 0' }}>
                {formatRupees(receivableCommissions.reduce((sum, c) => sum + (c.charge_amount || 0), 0))}
              </p>
              <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                {receivableCommissions.filter(c => c.paid_status === 'pending').length} pending collection
              </div>
            </div>
          )}

          {/* Pending Payable Card */}
          <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b' }}>Pending Payments</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f59e0b', margin: '0.5rem 0' }}>
              {formatRupees(pendingPayable.reduce((sum, c) => sum + (c.charge_amount || 0), 0))}
            </p>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              {pendingPayable.length} charges to settle
            </div>
          </div>

          {/* Paid History Card */}
          <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b' }}>Paid History</h3>
            <p style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6', margin: '0.5rem 0' }}>
              {formatRupees(paidPayable.reduce((sum, c) => sum + (c.charge_amount || 0), 0))}
            </p>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
              {paidPayable.length} settled charges
            </div>
          </div>
        </div>

        {facility_charges.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><DollarSign /></div>
            <h3>No facility charges yet</h3>
            <p>Facility charges will appear when appointments with associated doctors are created</p>
          </div>
        ) : (
          <>
            {/* MY EARNINGS SECTION - Receivable FacilityCharges */}
            {receivableCommissions.length > 0 && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600', color: '#059669' }}>
                  💰 My Facility Earnings
                </h2>

                {/* Desktop Table View */}
                <div className="table-container desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>Associated Doctor</th>
                        <th>Phone</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Payment Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receivablePagination.items.map((comm) => (
                        <tr key={comm.id} className={comm._synced === false ? 'entity-row-unsynced' : ''} onClick={() => navigate(`/facility-charges/${comm.id}`)} style={{ cursor: 'pointer' }}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {comm.associated_doctor_name || "Unknown"}
                              <SyncIndicator show={comm._synced === false} />
                            </div>
                          </td>
                          <td>{comm.associated_doctor_phone || "-"}</td>
                          <td style={{ fontWeight: '600', color: '#059669' }}>{formatRupees(comm.charge_amount)}</td>
                          <td>
                            <span className={`badge ${comm.paid_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}>
                              {comm.paid_status}
                            </span>
                          </td>
                          <td>{comm.payment_date ? new Date(comm.payment_date).toLocaleDateString() : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-only">
                  {receivablePagination.items.map((comm) => (
                    <div
                      key={comm.id}
                      className={comm._synced === false ? 'entity-card-unsynced' : ''}
                      onClick={() => navigate(`/facility-charges/${comm.id}`)}
                      style={{
                        padding: '1rem',
                        background: comm._synced === false ? undefined : '#f0fdf4',
                        borderRadius: '8px',
                        marginBottom: '0.75rem',
                        borderLeft: '3px solid #10b981',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          From: {comm.associated_doctor_name || "Unknown"}
                          <SyncIndicator show={comm._synced === false} />
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          {comm.associated_doctor_phone || "-"}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>
                          {formatRupees(comm.charge_amount)}
                        </div>
                        <span className={`badge ${comm.paid_status === 'paid' ? 'badge-paid' : 'badge-pending'}`}>
                          {comm.paid_status}
                        </span>
                      </div>
                      {comm.payment_date && (
                        <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.5rem' }}>
                          Received: {new Date(comm.payment_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <TablePagination
                  pagination={receivablePagination}
                  isMobile={window.innerWidth < 768}
                />
              </div>
            )}

            {/* PENDING PAYMENTS SECTION - Only for treating doctors */}
            {pendingPayable.length > 0 && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '1rem' }}>
                  <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Pending Payments</h2>
                  {selectedIds.length > 0 && (
                    <Button
                      data-testid="mark-paid-button"
                      onClick={() => setShowPayDialog(true)}
                      style={{
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        padding: '0.75rem 1.5rem',
                        fontSize: '1rem',
                        fontWeight: '600',
                        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
                      }}
                    >
                      <CheckCircle size={20} style={{ marginRight: '0.5rem' }} />
                      Click to Confirm Settlement ({selectedIds.length})
                    </Button>
                  )}
                </div>

                {/* Desktop Table View */}
                <div className="table-container desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: '50px' }}></th>
                        <th>Payable To</th>
                        <th>Phone</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingPagination.items.map((comm) => (
                        <tr key={comm.id} data-testid={`facility_charge-row-${comm.id}`} className={comm._synced === false ? 'entity-row-unsynced' : ''} onClick={() => navigate(`/facility-charges/${comm.id}`)} style={{ cursor: 'pointer' }}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              data-testid={`select-${comm.id}`}
                              checked={selectedIds.includes(comm.id)}
                              onCheckedChange={() => toggleSelection(comm.id)}
                            />
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {comm.receiver_doctor_name || "Unknown"}
                              <SyncIndicator show={comm._synced === false} />
                            </div>
                          </td>
                          <td>{comm.receiver_doctor_phone || "-"}</td>
                          <td>{formatRupees(comm.charge_amount)}</td>
                          <td>
                            <span className="badge badge-pending">{comm.paid_status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-only">
                  {pendingPagination.items.map((comm) => (
                    <div
                      key={comm.id}
                      data-testid={`facility_charge-card-${comm.id}`}
                      className={comm._synced === false ? 'entity-card-unsynced' : ''}
                      onClick={() => navigate(`/facility-charges/${comm.id}`)}
                      style={{
                        padding: '1rem',
                        background: comm._synced === false ? undefined : '#f8fafc',
                        borderRadius: '8px',
                        marginBottom: '0.75rem',
                        borderLeft: comm._synced === false ? undefined : '3px solid #fbbf24',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            To: {comm.receiver_doctor_name || "Unknown"}
                            <SyncIndicator show={comm._synced === false} />
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                            {comm.receiver_doctor_phone || "-"}
                          </div>
                        </div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            data-testid={`select-${comm.id}`}
                            checked={selectedIds.includes(comm.id)}
                            onCheckedChange={() => toggleSelection(comm.id)}
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0ea5e9' }}>
                          {formatRupees(comm.charge_amount)}
                        </div>
                        <span className="badge badge-pending">{comm.paid_status}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <TablePagination
                  pagination={pendingPagination}
                  isMobile={window.innerWidth < 768}
                />
              </div>
            )}

            {/* PAYMENT HISTORY - Only for treating doctors */}
            {paidPayable.length > 0 && (
              <div className="card">
                <h2 style={{ marginBottom: '1rem', fontSize: '1.25rem', fontWeight: '600' }}>Payment History</h2>

                {/* Desktop Table View */}
                <div className="table-container desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>Paid To</th>
                        <th>Phone</th>
                        <th>Amount</th>
                        <th>Payment Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidPagination.items.map((comm) => (
                        <tr key={comm.id} className={comm._synced === false ? 'entity-row-unsynced' : ''} onClick={() => navigate(`/facility-charges/${comm.id}`)} style={{ cursor: 'pointer' }}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              {comm.receiver_doctor_name || "Unknown"}
                              <SyncIndicator show={comm._synced === false} />
                            </div>
                          </td>
                          <td>{comm.receiver_doctor_phone || "-"}</td>
                          <td>{formatRupees(comm.charge_amount)}</td>
                          <td>{comm.payment_date ? new Date(comm.payment_date).toLocaleDateString() : "-"}</td>
                          <td>
                            <span className="badge badge-paid">{comm.paid_status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="mobile-only">
                  {paidPagination.items.map((comm) => (
                    <div
                      key={comm.id}
                      className={comm._synced === false ? 'entity-card-unsynced' : ''}
                      onClick={() => navigate(`/facility-charges/${comm.id}`)}
                      style={{
                        padding: '1rem',
                        background: comm._synced === false ? undefined : '#f8fafc',
                        borderRadius: '8px',
                        marginBottom: '0.75rem',
                        borderLeft: comm._synced === false ? undefined : '3px solid #10b981',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ marginBottom: '0.75rem' }}>
                        <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          To: {comm.receiver_doctor_name || "Unknown"}
                          <SyncIndicator show={comm._synced === false} />
                        </div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          {comm.receiver_doctor_phone || "-"}
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#10b981' }}>
                          {formatRupees(comm.charge_amount)}
                        </div>
                        <span className="badge badge-paid">{comm.paid_status}</span>
                      </div>
                      {comm.payment_date && (
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          Paid: {new Date(comm.payment_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <TablePagination
                  pagination={paidPagination}
                  isMobile={window.innerWidth < 768}
                />
              </div>
            )}
          </>
        )}

        <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
          <DialogContent data-testid="payment-dialog">
            <DialogHeader>
              <DialogTitle>Record Settlement</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePayment}>
              <div style={{ marginBottom: '1rem' }}>
                <Label htmlFor="payment_date">Settlement Date</Label>
                <Input
                  id="payment_date"
                  data-testid="payment-date-input"
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                />
              </div>
              <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Selected Charges</div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                  {formatRupees(selectedIds.reduce((sum, id) => {
                    const comm = facility_charges.find(c => c.id === id);
                    return sum + (comm?.charge_amount || 0);
                  }, 0))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)}>
                  Cancel
                </Button>
                <Button
                  data-testid="confirm-payment-button"
                  type="submit"
                  style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                >
                  Confirm Settlement
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

FacilityCharges.propTypes = {
  onLogout: PropTypes.func
};